import {
  BankingApplicationDocument,
  DO_APPLICATION_TYPE,
  emailRegex,
  generalTextRegex,
  maskSSN,
  phoneFormatter,
  phoneRegex,
  ssnFormatter,
  ssnOrItinRegex,
  SUFFIX_OPTIONS,
  BANKING_JOB_TITLE_OPTIONS,
  useSectionFormHelpers,
  zipRegex,
  bankingJobTitleRegex,
  STATE_OPTIONS_WITH_ARMED_FORCES,
  OptionalString,
} from 'client-experience-toolbox';
import dayjs from 'dayjs';
import {
  DatePickers,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from 'drift-ui';
import { useApplication } from 'hooks/useApplication';
import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import { sendDOFirebaseEvent } from 'utils/utils';
import baseStyles from './BaseFormStyles.scss';
import styles from './ControlProng.scss';
import ContinueButton from 'components/ContinueButton';
import cx from 'classnames';

export const ControlProng: FC = () => {
  const { application } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const applicationData = application?.applicationData as BankingApplicationDocument['applicationData'];
  const controlProng = applicationData?.controlProng;
  const isApplicant = applicationData?.controlProng?.key !== 'controlProng';
  const ssnValue = applicationData?.controlProng?.ssnOrItin ?? '';
  const [editingSSN, setEditingSSN] = useState(false);
  const [isPatching, setIsPatching] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const controlProngOptions =
    applicationData?.applicants?.map((applicant, idx) => ({
      label:
        idx === 0
          ? `You (${applicant?.firstName} ${applicant?.lastName})`
          : `${applicant?.firstName} ${applicant?.lastName}`,
      value: idx,
      fieldValue: applicant?.applicantId || applicant?.key || '',
    })) || [];
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.BANKING);
  const [errors, setErrors] = React.useState<Record<string, string | undefined>>({});
  const { t: bankingT } = useTranslation('common', {
    keyPrefix: 'digital_onboarding.banking',
  });
  controlProngOptions.push({
    label: bankingT('control_prong.someone_else_option'),
    value: controlProngOptions.length,
    fieldValue: 'controlProng',
  });
  const { t } = useTranslation();
  const { patchApplication, patchFullApplication } = useFormNetworkCalls(DO_APPLICATION_TYPE.BANKING);

  const hasSSNError = Boolean(errors['applicationData.controlProng.ssnOrItin']);
  let displaySSN = ssnValue;
  if (editingSSN || hasSSNError) {
    displaySSN = ssnFormatter(displaySSN);
  } else if (controlProng?.ssnOrItinDisplay?.length) {
    displaySSN = maskSSN(controlProng.ssnOrItinDisplay, true);
  } else {
    displaySSN = maskSSN(displaySSN);
  }

  const {
    presenceValidatorGenerator,
    regexValidatorGenerator,
    dateRangeValidatorGenerator,
    selectDropdownHandlerGenerator,
    clearValidation,
    numberHandlerGenerator,
    textHandlerGenerator,
    dateHandlerGenerator,
  } = useSectionFormHelpers({
    application,
    updateApplication,
    patchApplication,
    errorSetter: setErrors,
    fireBaseLogEvent: sendDOFirebaseEvent,
  });

  const currentIndex = () => {
    const applicantControlProngIndex = applicationData?.applicants?.findIndex((applicant, idx) => {
      if (idx === 0) return applicant.applicantId === controlProng?.key;
      return applicant.key === controlProng?.key;
    });
    const someoneElseIndex = controlProngOptions.length - 1;
    if (applicantControlProngIndex !== undefined && applicantControlProngIndex > -1) {
      return applicantControlProngIndex;
    } else {
      return controlProng?.key === 'controlProng' ? someoneElseIndex : -1;
    }
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const fieldValue = controlProngOptions[value].fieldValue;
    if (fieldValue === 'controlProng') {
      setErrors({});
      updateApplication('applicationData.controlProng', { key: 'controlProng' });
      await patchApplication('applicationData.controlProng', { key: 'controlProng' });
    } else {
      setErrors({});
      updateApplication('applicationData.controlProng', { key: fieldValue });
      await patchApplication('applicationData.controlProng', { key: fieldValue });
    }
    clearValidation('applicationData.controlProng.key');
    return controlProngOptions[value];
  };

  const fieldValidators: { [key: string]: () => boolean } = {
    key: presenceValidatorGenerator({
      key: 'applicationData.controlProng.key',
      errorText: bankingT('business_type.required'),
    }),
    jobTitle: regexValidatorGenerator({
      key: 'applicationData.controlProng.jobTitle',
      regex: bankingJobTitleRegex,
      errorText: bankingT('business_type.required'),
      skip: !controlProng?.key,
    }),
    firstName: regexValidatorGenerator({
      key: 'applicationData.controlProng.firstName',
      regex: generalTextRegex,
      errorText: bankingT('business_type.required'),
      skip: isApplicant || !controlProng?.key,
    }),
    middleName: regexValidatorGenerator({
      key: 'applicationData.controlProng.middleName',
      regex: generalTextRegex,
      errorText: '',
      optional: true,
      skip: isApplicant || !controlProng?.key,
    }),
    lastName: regexValidatorGenerator({
      key: 'applicationData.controlProng.lastName',
      regex: generalTextRegex,
      errorText: bankingT('business_type.required'),
      skip: isApplicant || !controlProng?.key,
    }),
    ssnOrItin: regexValidatorGenerator({
      key: 'applicationData.controlProng.ssnOrItin',
      regex: ssnOrItinRegex,
      errorText: bankingT('owner_info.invalid_ssn'),
      skip: Boolean(applicationData?.controlProng?.ssnOrItinDisplay) || isApplicant || !controlProng?.key,
      skipPatch: true,
    }),
    dateOfBirth: dateRangeValidatorGenerator({
      key: 'applicationData.controlProng.dateOfBirth',
      range: [undefined, dayjs()],
      errorText: bankingT('business_type.required'),
      skip: isApplicant || !controlProng?.key,
    }),
    businessStreetAddress: regexValidatorGenerator({
      key: 'applicationData.controlProng.homeAddress.street',
      regex: generalTextRegex,
      errorText: bankingT('business_type.required'),
      skip: isApplicant || !controlProng?.key,
    }),
    secondaryStreetAddress: regexValidatorGenerator({
      key: 'applicationData.controlProng.homeAddress.secondaryStreet',
      regex: generalTextRegex,
      errorText: '',
      optional: true,
      skip: isApplicant || !controlProng?.key,
    }),
    tertiaryStreetAddress: regexValidatorGenerator({
      key: 'applicationData.controlProng.homeAddress.tertiaryStreet',
      regex: generalTextRegex,
      errorText: '',
      optional: true,
      skip: isApplicant || !controlProng?.key,
    }),
    city: regexValidatorGenerator({
      key: 'applicationData.controlProng.homeAddress.city',
      regex: generalTextRegex,
      errorText: bankingT('business_type.required'),
      skip: isApplicant || !controlProng?.key,
    }),
    state: presenceValidatorGenerator({
      key: 'applicationData.controlProng.homeAddress.state',
      errorText: bankingT('business_type.required'),
      skip: isApplicant || !controlProng?.key,
    }),
    zipCode: regexValidatorGenerator({
      key: 'applicationData.controlProng.homeAddress.zip',
      regex: zipRegex,
      errorText: bankingT('owner_info.invalid_zip'),
      skip: isApplicant || !controlProng?.key,
    }),
    email: regexValidatorGenerator({
      key: `applicationData.controlProng.email`,
      regex: emailRegex,
      errorText: controlProng?.email
        ? t('digital_onboarding.common_section.invalid_email')
        : bankingT('business_type.required'),
      makeLowerCase: true,
      skip: isApplicant || !controlProng?.key,
      additionalErrorCheck: (value: OptionalString) => {
        if (!value) return undefined;
        const applicantEmails = applicationData?.applicants?.map((app) => app.email?.toLowerCase());

        return applicantEmails?.includes(value.toLowerCase())
          ? t('digital_onboarding.owner_officer_section.email_duplicate')
          : undefined;
      },
    }),
    businessPhone: regexValidatorGenerator({
      key: 'applicationData.controlProng.mobilePhoneNumber',
      regex: phoneRegex,
      errorText: !applicationData?.controlProng?.mobilePhoneNumber
        ? bankingT('business_type.required')
        : t('digital_onboarding.banking.owner_info.invalid_phone'),
      skip: isApplicant || !controlProng?.key,
    }),
  };

  const scrollToFirstError = () => {
    const firstErrorInput = formRef.current?.querySelector('.Mui-error') as HTMLElement;
    if (firstErrorInput) {
      firstErrorInput.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      firstErrorInput.focus({ preventScroll: true });
    }
  };

  const onContinue = async () => {
    const results = Object.values(fieldValidators).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);

    if (firstInvalid) {
      scrollToFirstError();
      return;
    }
    await patchFullApplication(setIsPatching);
  };

  const onChangeSSN = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let value = event.target.value;
    if (!editingSSN && !hasSSNError) {
      const isAppend = value.startsWith(displaySSN);
      const isBackspace = !isAppend && value.startsWith(displaySSN.slice(0, -1));

      if (isAppend) value = value.slice(displaySSN.length);
      else if (isBackspace) value = '';
    }

    // Convert to only digits and then format to remove extra dashes
    value = value.replace(/[^0-9]/g, '');
    value = ssnFormatter(value);

    if (value.length > 11) value = value.slice(0, 11);

    updateApplication('applicationData.controlProng.ssnOrItin', value);
    setEditingSSN(true);
    clearValidation('applicationData.controlProng.ssnOrItin');
    updateApplication('applicationData.controlProng.ssnOrItinDisplay', '');
  };

  const onBlurSSN = async () => {
    const isValid = fieldValidators.ssnOrItin();
    setEditingSSN(false);
    updateApplication('applicationData.controlProng.ssnOrItin', ssnValue);

    if (isValid && patchApplication) {
      updateApplication(`applicationData.controlProng.ssnOrItinDisplay`, ssnValue.slice(-4));
      await patchApplication('applicationData.controlProng.ssnOrItin', ssnValue, controlProng?.key);
    }
  };

  return (
    <div ref={formRef} className={cx(baseStyles.formContainer, styles.controlProngContainer)}>
      <div className={baseStyles.formFieldContainer}>
        <div>
          <p className={cx(styles.titleText, errors['applicationData.controlProng.key'] && styles.redTitle)}>
            {bankingT('control_prong.title')}
          </p>
          <p className={styles.descriptionText}>{bankingT('control_prong.description')}</p>
          <FormControl error={!!errors['applicationData.company.ownershipType']}>
            <RadioGroup value={currentIndex()} onChange={handleChange} data-nid-target="control-prong-selection">
              {controlProngOptions.map((type) => (
                <div key={type.value}>
                  <FormControlLabel
                    value={type.value}
                    control={
                      <Radio
                        slotProps={{
                          input: {
                            'data-nid-target': `control-prong-selection-${type.value}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    label={type.label}
                  />
                </div>
              ))}
            </RadioGroup>
          </FormControl>
          {errors['applicationData.controlProng.key'] && (
            <p className={styles.requiredText}>{errors['applicationData.controlProng.key']}</p>
          )}
        </div>
        {controlProng?.key === 'controlProng' ? (
          <div className={baseStyles.formFieldContainer}>
            <p className={styles.titleText}>{bankingT('control_prong.someone_else_title')}</p>
            <TextField
              label={t('digital_onboarding.owner_officer_section.label_first_name')}
              value={controlProng?.firstName || ''}
              onChange={textHandlerGenerator('applicationData.controlProng.firstName', clearValidation)}
              onBlur={fieldValidators.firstName}
              helperText={errors['applicationData.controlProng.firstName']}
              error={!!errors['applicationData.controlProng.firstName']}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                  'data-nid-target': 'control-prong-first-name',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <TextField
              label={bankingT('owner_info.label_middle_name')}
              value={controlProng?.middleName || ''}
              onChange={textHandlerGenerator('applicationData.controlProng.middleName', clearValidation)}
              onBlur={fieldValidators.middleName}
              error={!!errors['applicationData.controlProng.middleName']}
              helperText={errors['applicationData.controlProng.middleName']}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                  'data-nid-target': 'control-prong-middle-name',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <TextField
              label={t('digital_onboarding.owner_officer_section.label_last_name')}
              value={controlProng?.lastName || ''}
              onChange={textHandlerGenerator('applicationData.controlProng.lastName', clearValidation)}
              onBlur={fieldValidators.lastName}
              error={!!errors['applicationData.controlProng.lastName']}
              helperText={errors['applicationData.controlProng.lastName']}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                  'data-nid-target': 'control-prong-last-name',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <div className={baseStyles.groupedFields}>
              <FormControl fullWidth error={!!errors['applicationData.controlProng.suffix']}>
                <InputLabel id="suffix-label">{bankingT('owner_info.label_suffix')}</InputLabel>
                <Select
                  labelId="suffix-label"
                  label={bankingT('owner_info.label_suffix')}
                  value={controlProng?.suffix || ''}
                  onChange={selectDropdownHandlerGenerator('applicationData.controlProng.suffix', clearValidation)}
                  slotProps={{
                    input: { 'data-nid-target': 'control-prong-suffix' } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                >
                  {SUFFIX_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
                {errors[`applicationData.controlProng.suffix`] && (
                  <FormHelperText>{errors[`applicationData.controlProng.suffix`]}</FormHelperText>
                )}
              </FormControl>
              <DatePickers.DatePicker
                label={t('digital_onboarding.owner_officer_section.label_date_of_birth')}
                value={controlProng?.dateOfBirth ? dayjs(controlProng.dateOfBirth) : undefined}
                onChange={dateHandlerGenerator('applicationData.controlProng.dateOfBirth', clearValidation)}
                onAccept={fieldValidators.dateOfBirth}
                disableFuture
                slotProps={{
                  textField: {
                    error: !!errors['applicationData.controlProng.dateOfBirth'],
                    helperText: errors['applicationData.controlProng.dateOfBirth'] ?? '',
                    onBlur: fieldValidators.dateOfBirth,
                    inputProps: {
                      'data-nid-target': 'control-prong-date-of-birth',
                    },
                  } as Record<string, unknown>,
                }}
                sx={{ width: '100%' }}
                openTo="year"
                views={['year', 'month', 'day']}
              />
            </div>
            <TextField
              label={bankingT('owner_info.label_ssn')}
              value={displaySSN}
              onBlur={onBlurSSN}
              onChange={onChangeSSN}
              error={hasSSNError}
              helperText={errors['applicationData.controlProng.ssnOrItin']}
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  'data-nid-target': 'control-prong-ssn',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <FormControl fullWidth error={!!errors['applicationData.controlProng.jobTitle']}>
              <InputLabel id="job-title-label">
                {t('digital_onboarding.owner_officer_section.label_job_title')}
              </InputLabel>
              <Select
                labelId="job-title-label"
                label={t('digital_onboarding.owner_officer_section.label_job_title')}
                value={controlProng?.jobTitle && controlProng.jobTitle !== 'Owner' ? controlProng.jobTitle : ''}
                onChange={selectDropdownHandlerGenerator('applicationData.controlProng.jobTitle', clearValidation)}
                slotProps={{
                  input: {
                    'data-nid-target': 'control-prong-job-title',
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              >
                {BANKING_JOB_TITLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
              {errors[`applicationData.controlProng.jobTitle`] && (
                <FormHelperText>{errors[`applicationData.controlProng.jobTitle`]}</FormHelperText>
              )}
            </FormControl>
            <div>
              <p className={styles.invalidAddressesText}>{bankingT('owner_info.po_boxes')}</p>
              <TextField
                label={bankingT('business_info.label_street')}
                value={controlProng?.homeAddress?.street || ''}
                onChange={textHandlerGenerator('applicationData.controlProng.homeAddress.street', clearValidation)}
                onBlur={fieldValidators.businessStreetAddress}
                error={!!errors['applicationData.controlProng.homeAddress.street']}
                helperText={errors['applicationData.controlProng.homeAddress.street']}
                slotProps={{
                  htmlInput: {
                    maxLength: 500,
                    'data-nid-target': 'control-prong-street',
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            </div>
            <TextField
              label={bankingT('business_info.label_secondary_street')}
              value={controlProng?.homeAddress?.secondaryStreet || ''}
              onChange={textHandlerGenerator(
                'applicationData.controlProng.homeAddress.secondaryStreet',
                clearValidation,
              )}
              onBlur={fieldValidators.secondaryStreetAddress}
              error={!!errors['applicationData.controlProng.homeAddress.secondaryStreet']}
              helperText={errors['applicationData.controlProng.homeAddress.secondaryStreet']}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                  'data-nid-target': 'control-prong-secondary-street',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <TextField
              label={bankingT('business_info.label_tertiary_street')}
              value={controlProng?.homeAddress?.tertiaryStreet || ''}
              onChange={textHandlerGenerator(
                'applicationData.controlProng.homeAddress.tertiaryStreet',
                clearValidation,
              )}
              onBlur={fieldValidators.tertiaryStreetAddress}
              error={!!errors['applicationData.controlProng.homeAddress.tertiaryStreet']}
              helperText={errors['applicationData.controlProng.homeAddress.tertiaryStreet']}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                  'data-nid-target': 'control-prong-tertiary-street',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <div className={baseStyles.groupedFields}>
              <TextField
                label={t('digital_onboarding.common_section.label_city')}
                value={controlProng?.homeAddress?.city || ''}
                onChange={textHandlerGenerator('applicationData.controlProng.homeAddress.city', clearValidation)}
                onBlur={fieldValidators.city}
                autoCapitalize="sentences"
                error={!!errors['applicationData.controlProng.homeAddress.city']}
                helperText={errors['applicationData.controlProng.homeAddress.city']}
                slotProps={{
                  htmlInput: {
                    maxLength: 500,
                    'data-nid-target': 'control-prong-city',
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
              <FormControl fullWidth error={!!errors['applicationData.controlProng.homeAddress.state']}>
                <InputLabel id="state-label">{t('digital_onboarding.common_section.label_state')}</InputLabel>
                <Select
                  labelId="state-label"
                  label={t('digital_onboarding.common_section.label_state')}
                  value={controlProng?.homeAddress?.state || ''}
                  onChange={selectDropdownHandlerGenerator(
                    'applicationData.controlProng.homeAddress.state',
                    clearValidation,
                  )}
                  slotProps={{
                    input: { 'data-nid-target': 'control-prong-state' } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                >
                  {STATE_OPTIONS_WITH_ARMED_FORCES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
                {errors[`applicationData.controlProng.homeAddress.state`] && (
                  <FormHelperText>{errors[`applicationData.controlProng.homeAddress.state`]}</FormHelperText>
                )}
              </FormControl>
            </div>
            <div className={baseStyles.groupedFields}>
              <TextField
                label={t('digital_onboarding.common_section.label_zip')}
                value={controlProng.homeAddress?.zip || ''}
                onChange={numberHandlerGenerator('applicationData.controlProng.homeAddress.zip', clearValidation)}
                onBlur={fieldValidators.zipCode}
                error={!!errors['applicationData.controlProng.homeAddress.zip']}
                helperText={errors['applicationData.controlProng.homeAddress.zip']}
                slotProps={{
                  htmlInput: {
                    maxLength: 5,
                    inputMode: 'numeric',
                    'data-nid-target': 'control-prong-zip',
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
              <TextField
                label={t('Country')}
                value={'USA'}
                disabled={true}
                helperText="Address must be in the US."
                slotProps={{
                  htmlInput: {
                    'data-nid-target': 'control-prong-country',
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            </div>
            <TextField
              label={bankingT('owner_info.label_email')}
              value={applicationData?.controlProng?.email || ''}
              onChange={textHandlerGenerator('applicationData.controlProng.email', clearValidation, true)}
              onBlur={fieldValidators.email}
              error={!!errors['applicationData.controlProng.email']}
              helperText={errors['applicationData.controlProng.email']}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                  'data-nid-target': 'control-prong-email',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
            <TextField
              label={t('digital_onboarding.common_section.label_personal_phone')}
              value={phoneFormatter(applicationData?.controlProng?.mobilePhoneNumber) || ''}
              onChange={numberHandlerGenerator('applicationData.controlProng.mobilePhoneNumber', clearValidation)}
              onBlur={fieldValidators.businessPhone}
              error={!!errors['applicationData.controlProng.mobilePhoneNumber']}
              helperText={errors['applicationData.controlProng.mobilePhoneNumber']}
              slotProps={{
                htmlInput: {
                  maxLength: 16,
                  inputMode: 'numeric',
                  'data-nid-target': 'control-prong-phone',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
          </div>
        ) : (
          controlProng?.key && (
            <div className={styles.jobTitleContainer}>
              <p className={styles.titleText}>
                {bankingT('control_prong.job_title_question', {
                  control_prong: controlProngOptions[currentIndex()]?.label,
                })}
              </p>
              <div>
                <FormControl fullWidth error={!!errors['applicationData.controlProng.jobTitle']}>
                  <InputLabel id="job-title-label">
                    {t('digital_onboarding.owner_officer_section.label_job_title')}
                  </InputLabel>
                  <Select
                    labelId="job-title-label"
                    label={t('digital_onboarding.owner_officer_section.label_job_title')}
                    value={controlProng?.jobTitle && controlProng.jobTitle !== 'Owner' ? controlProng.jobTitle : ''}
                    onChange={selectDropdownHandlerGenerator('applicationData.controlProng.jobTitle', clearValidation)}
                    slotProps={{
                      input: {
                        'data-nid-target': 'control-prong-applicant-job-title',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  >
                    {BANKING_JOB_TITLE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors[`applicationData.controlProng.jobTitle`] && (
                    <FormHelperText>{errors[`applicationData.controlProng.jobTitle`]}</FormHelperText>
                  )}
                </FormControl>
              </div>
            </div>
          )
        )}
      </div>
      <ContinueButton
        buttonStyles={baseStyles.buttonStyle}
        title={t('digital_onboarding.next')}
        handleContinueClick={onContinue}
        isPatching={isPatching}
      />
    </div>
  );
};
